import _ from 'lodash';
import { bindServices } from '@/services/api';
import { toPaginate, getFiltersGlobalFilters } from '@/utils/infrastructure';
import { VALIDATION_INTERNAL_ERRORS } from '@/utils/infrastructure/constants';
import {
  buildCompanyConfigPatchPayload,
  parseOptionalNumericEnumFromApi,
  pickCompanyConfigurationsFromResponse,
} from './utils/companyConfigUtils';
import { filtersConfig } from './utils';
import { CoreEnums } from '@/utils/domain/constants';
import { FETCH_ROUTE, MODEL_NAME } from './constants';

const namespace = MODEL_NAME;
const route = FETCH_ROUTE;
const companyConfigRoute = 'company/config';
const api = bindServices(namespace);
const {
  BulkActionType,
  MfaRequirementPolicy,
  CompanyAccessPolicyEvaluationMode,
  CompanyAccessPolicyDefaultDecision,
} = CoreEnums;

const initialPagination = { page: 1, count: 0, totalPages: 0 };
const initialState = {
  list: [],
  pagination: initialPagination,
  orderBy: 'updated desc',
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  validationErrors: [],
  modelDelete: namespace,
  filtersConfig,
  restoreAccessPolicyModal: {
    visible: false,
    message: '',
    accessPolicyIds: null,
  },
  companyGlobalSettings: {
    mfaPolicy: undefined,
    evaluationMode: undefined,
    defaultDecision: undefined,
  },
  companyGlobalSettingsLoading: true,
  activatingItemIds: {},
  rowActionLoadingItemIds: {},
};

const AccessPoliciesModel = {
  namespace,
  state: initialState,
  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const res = yield call(api.getAll, {
          route: `${route}/paged`,
          params: {
            limit: pageSize,
            page: 1,
            orderBy: 'updated desc',
            ...payload,
          },
          readPriority: true,
        });
        yield put({
          type: 'queryData',
          payload: { ...res, limit: payload?.limit ?? pageSize, page: payload?.page ?? 1 },
        });
      } catch (e) {}
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'successReducer', payload: { success: false } });
        const res = yield call(api.getById, {
          route,
          id: payload.id,
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {}
    },

    *fetchCreate({ payload }, { call, put }) {
      try {
        yield put({ type: 'successReducer', payload: { success: false } });
        yield call(api.create, { route, data: payload.data });
        yield put({ type: 'successReducer', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        const validationErrors = e?.validationErrors || [];
        const itemDeletedError = validationErrors.find(
          (ve) =>
            ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
            ve.metadata?.id,
        );
        if (itemDeletedError) {
          const message =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || e?.message;
          const accessPolicyId = itemDeletedError.metadata.id;
          yield put({
            type: 'showRestoreAccessPolicyModal',
            payload: { message, accessPolicyId },
          });
          return;
        }
        if (e?.message) {
          if (e.validationErrors?.[0] && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errorsReducer',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { call, put }) {
      try {
        yield put({ type: 'successReducer', payload: { success: false } });
        yield call(api.update, { route, data: payload.data });
        yield put({ type: 'successReducer', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        const validationErrors = e?.validationErrors || [];
        const itemDeletedError = validationErrors.find(
          (ve) =>
            ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
            ve.metadata?.id,
        );
        if (itemDeletedError) {
          const message =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || e?.message;
          const accessPolicyId = itemDeletedError.metadata.id;
          yield put({
            type: 'showRestoreAccessPolicyModal',
            payload: { message, accessPolicyId },
          });
          return;
        }
        if (e?.message) {
          if (e.validationErrors?.[0] && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errorsReducer',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchDelete({ payload }, { call, put }) {
      try {
        const { id, view } = payload;
        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null, modelDelete: null },
        });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: { state: false, item: null, detail: false, id: null },
          });
        }
        yield call(api.remove, { route, id });
        yield put({ type: 'successReducer', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkDeleteAccessPolicies({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;
        yield call(api.bulkAction, {
          route,
          bulkActionType: BulkActionType.Delete,
          ids,
        });
        const pageSize = yield select((state) => state.global.pageSize);
        const moduleState = yield select((state) => state[namespace]);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(moduleState.orderBy && { orderBy: moduleState.orderBy }),
          ...(filters && { filter: filters }),
        };
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *duplicatePolicy({ payload }, { call, put }) {
      const id = payload?.id;
      yield put({ type: 'queryRowActionLoading', payload: { id, active: true } });
      try {
        yield call(api.apiRoute, {
          route: `${route}/duplicate/${id}`,
          method: 'post',
        });
        yield put({ type: 'successReducer', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        const validationErrors = e?.validationErrors || [];
        const itemDeletedError = validationErrors.find(
          (ve) =>
            ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
            ve.metadata?.id,
        );
        if (itemDeletedError) {
          const message =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || e?.message;
          const accessPolicyId = itemDeletedError.metadata.id;
          yield put({
            type: 'showRestoreAccessPolicyModal',
            payload: { message, accessPolicyId },
          });
          return;
        }
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      } finally {
        yield put({ type: 'queryRowActionLoading', payload: { id, active: false } });
      }
    },

    *fetchActivate({ payload }, { call, put }) {
      const { id, state: active } = payload;
      yield put({ type: 'queryActivatingItem', payload: { id, active: true } });
      try {
        yield call(api.activate, {
          route,
          id,
          isActive: active,
        });
        yield put({ type: 'policyActivate', payload });
      } catch (error) {
        /*  */
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { id, active: false } });
      }
    },

    *restoreAccessPolicy({ payload }, { call, put, select }) {
      try {
        const { accessPolicyId } = payload;
        const ids = Array.isArray(accessPolicyId) ? accessPolicyId : [accessPolicyId];
        yield call(api.apiRoute, {
          route: `${route}/restore`,
          method: 'post',
          data: ids,
        });
        yield put({ type: 'closeRestoreAccessPolicyModal' });
        yield put({
          type: 'queryShowForm',
          payload: { state: false, item: null, detail: false, id: null },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        const pageSize = yield select((state) => state.global.pageSize);
        const moduleState = yield select((state) => state[namespace]);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const refetchParams = {
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(moduleState.orderBy && { orderBy: moduleState.orderBy }),
          ...(filters && { filter: filters }),
        };
        yield put({ type: 'fetchList', payload: refetchParams });
      } catch (err) {
        if (err?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: err.message },
          });
        }
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'successReducer', payload });
    },
    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },

    *fetchCompanyConfig(_, { call, put }) {
      yield put({ type: 'setCompanyGlobalSettingsLoading', payload: true });
      try {
        const res = yield call(api.apiRoute, {
          route: companyConfigRoute,
          method: 'get',
          readPriority: true,
        });
        const configurations = pickCompanyConfigurationsFromResponse(res);
        yield put({ type: 'mergeCompanyGlobalSettingsFromApi', payload: configurations });
      } catch (e) {
        /* Keep selectors empty or last value if load fails */
      } finally {
        yield put({ type: 'setCompanyGlobalSettingsLoading', payload: false });
      }
    },

    *patchCompanyGlobalSetting({ payload }, { call, put, select }) {
      const { field, value } = payload;
      const prev = yield select((s) => s[namespace].companyGlobalSettings[field]);
      yield put({ type: 'setCompanyGlobalSettingField', payload: { field, value } });
      try {
        const data = buildCompanyConfigPatchPayload(field, value, CoreEnums);
        const hasPayload =
          data.mfaPolicy != null ||
          data.accessPolicy?.evaluationMode != null ||
          data.accessPolicy?.defaultDecision != null;
        if (!hasPayload) {
          yield put({ type: 'setCompanyGlobalSettingField', payload: { field, value: prev } });
          return;
        }
        yield call(api.apiRoute, {
          route: companyConfigRoute,
          method: 'patch',
          data,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        yield put({ type: 'setCompanyGlobalSettingField', payload: { field, value: prev } });
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },
  },
  reducers: {
    queryData(state, { payload }) {
      return {
        ...state,
        list: payload.items || [],
        pagination: _.merge({}, state.pagination, toPaginate(payload)),
      };
    },
    queryItem(state, { payload }) {
      return { ...state, item: payload };
    },
    policyActivate(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      return { ...state, list };
    },
    queryActivatingItem(state, { payload }) {
      const { id, ids, active } = payload || {};
      const activatingItemIds = { ...(state.activatingItemIds || {}) };
      if (Array.isArray(ids)) {
        ids.forEach((itemId) => {
          if (active) {
            activatingItemIds[itemId] = true;
          } else {
            delete activatingItemIds[itemId];
          }
        });
        return { ...state, activatingItemIds };
      }
      if (id == null) {
        return { ...state, activatingItemIds: {} };
      }
      if (active) {
        activatingItemIds[id] = true;
      } else {
        delete activatingItemIds[id];
      }
      return { ...state, activatingItemIds };
    },
    queryRowActionLoading(state, { payload }) {
      const rowActionLoadingItemIds = { ...(state.rowActionLoadingItemIds || {}) };
      const { id, active } = payload || {};
      if (!id) return state;
      if (active) {
        rowActionLoadingItemIds[id] = true;
      } else {
        delete rowActionLoadingItemIds[id];
      }
      return { ...state, rowActionLoadingItemIds };
    },
    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
      };
    },
    editOrDetail(state, { payload }) {
      return {
        ...state,
        item: payload.item ?? {},
      };
    },
    queryOrderBy(state, { payload }) {
      return { ...state, orderBy: payload };
    },
    successReducer(state, { payload }) {
      return {
        ...state,
        ...payload,
        success: payload?.success ?? payload,
      };
    },
    errorsReducer(state, { payload }) {
      return {
        ...state,
        validationErrors: payload.validationErrors || [],
        message: payload.message,
      };
    },
    showRestoreAccessPolicyModal(state, { payload }) {
      const accessPolicyIds =
        payload?.accessPolicyIds ??
        (payload?.accessPolicyId != null ? [payload.accessPolicyId] : null);
      return {
        ...state,
        restoreAccessPolicyModal: {
          visible: true,
          message: payload?.message || '',
          accessPolicyIds,
        },
      };
    },
    closeRestoreAccessPolicyModal(state) {
      return {
        ...state,
        restoreAccessPolicyModal: {
          visible: false,
          message: '',
          accessPolicyIds: null,
        },
      };
    },
    setCompanyGlobalSettingsLoading(state, { payload }) {
      return { ...state, companyGlobalSettingsLoading: !!payload };
    },
    mergeCompanyGlobalSettingsFromApi(state, { payload }) {
      const configurations = payload || {};
      const ap = configurations.accessPolicy || {};
      return {
        ...state,
        companyGlobalSettings: {
          mfaPolicy: parseOptionalNumericEnumFromApi(
            MfaRequirementPolicy,
            configurations.mfaPolicy,
          ),
          evaluationMode: parseOptionalNumericEnumFromApi(
            CompanyAccessPolicyEvaluationMode,
            ap.evaluationMode,
          ),
          defaultDecision: parseOptionalNumericEnumFromApi(
            CompanyAccessPolicyDefaultDecision,
            ap.defaultDecision,
          ),
        },
      };
    },
    setCompanyGlobalSettingField(state, { payload }) {
      const { field, value } = payload;
      return {
        ...state,
        companyGlobalSettings: {
          ...state.companyGlobalSettings,
          [field]: value,
        },
      };
    },
    reset() {
      return { ...initialState };
    },
  },
};

export default AccessPoliciesModel;
